import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import config from '../utils/Constants';
import { fetchAuthSession } from '@aws-amplify/auth';

export const medchartApi = createApi({
  reducerPath: 'medchartApi',

  baseQuery: fetchBaseQuery({
    baseUrl: config.getApiUrl(),
    prepareHeaders: async (headers) => {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }

      return headers;
    },
  }),

  tagTypes: ['Admissions', 'TrialSubjects'],

  endpoints: (builder) => ({
    getAdmissions: builder.query({
      query: ({ page = 1, pageSize = 10, search, icdCode, status, hasPdf } = {}) => {
        const params = new URLSearchParams({ page, page_size: pageSize });
        if (search) params.set('search', search);
        if (icdCode) params.set('icd_code', icdCode);
        if (status) params.set('status', status);
        if (hasPdf != null) params.set('has_pdf', String(hasPdf));
        return `/api/v1/client/admissions/?${params}`;
      },
      providesTags: ['Admissions'],
    }),

    getProcedures: builder.query({
      query: ({ query }) => {
        return `/medchart/procedures/search?limit=10&query=${query}`;
      },
    }),

    createAdmissions: builder.mutation({
      query: (payload) => {
        return {
          url: '/medchart/admission',
          method: 'POST',
          body: {
            procedure_icd_codes: [
              {
                icd_code: payload.icdCode,
                icd_version: payload.icdVersion,
              },
            ],
          },
        };
      },
    }),

    // story-007: section-level regeneration
    regenerateSection: builder.mutation({
      query: ({ admissionId, detailType }) => ({
        url: `/catalog/admissions/${admissionId}/regenerate`,
        method: 'POST',
        body: { detail_type: detailType },
      }),
    }),

    // ---- Life Sciences / Clinical Trial endpoints ----

    generateTrialSubjects: builder.mutation({
      query: (payload) => ({
        url: '/api/v1/client/trial-subjects/generate/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['TrialSubjects'],
    }),

    getTrialSubjects: builder.query({
      query: ({ protocolId } = {}) => {
        const params = protocolId ? `?protocol_id=${protocolId}` : '';
        return `/api/v1/client/trial-subjects/${params}`;
      },
      providesTags: ['TrialSubjects'],
    }),

    getTrialSubject: builder.query({
      query: (id) => `/api/v1/client/trial-subjects/${id}/`,
    }),
  }),
});

export const {
  useGetAdmissionsQuery,
  useGetProceduresQuery,
  useLazyGetProceduresQuery,
  useCreateAdmissionsMutation,
  useRegenerateSectionMutation,
  useGenerateTrialSubjectsMutation,
  useGetTrialSubjectsQuery,
  useGetTrialSubjectQuery,
} = medchartApi;
